import { useEffect, useState } from "react";

import { AdminShell } from "@/components/admin/admin-shell";
import { HeaderNavManager } from "@/components/admin/header-nav-manager";
import { apiGet } from "@/services/api";

/**
 * `app/admin/navigation/page.tsx`.
 *
 * The server component read `prisma.headerConfig` directly and fell back to
 * these defaults when the row was missing. `GET /api/admin/nav` runs the same
 * `ensureConfig()` fallback server-side, but the defaults are kept here too so
 * the screen still renders if the request fails.
 */
const DEFAULT_CONFIG = {
  showAllButton: true,
  showDeals: true,
  dealsLabel: "Today's Deals",
  dealsHref: "/search?deal=flash",
  showPrime: true,
  primeLabel: "Prime",
  primeHref: "/prime",
  showCompare: true,
  compareLabel: "Compare",
  compareHref: "/compare",
  customLinks: []
};

type NavCategory = { id: string; name: string; slug: string; sortOrder: number; showInNav: boolean };

type HeaderConfig = typeof DEFAULT_CONFIG & { customLinks: unknown[] };

export function AdminNavigationPage() {
  const [categories, setCategories] = useState<NavCategory[]>([]);
  const [config, setConfig] = useState<HeaderConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void apiGet<{ config?: HeaderConfig; categories?: NavCategory[] }>("/api/admin/nav")
      .then((data) => {
        if (!active) return;
        if (data?.categories) setCategories(data.categories);
        if (data?.config) setConfig({ ...DEFAULT_CONFIG, ...data.config });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <AdminShell
      title="Header Navigation"
      subtitle="Manage which categories and action links appear in the MOVA storefront sub-header bar."
    >
      {loading ? (
        <div className="flex h-64 items-center justify-center rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-amazon-orange border-t-transparent" />
            <span>Loading navigation settings...</span>
          </div>
        </div>
      ) : (
        <HeaderNavManager
          key="ready"
          initialCategories={categories}
          initialConfig={{
            showAllButton: config.showAllButton,
            showDeals: config.showDeals,
            dealsLabel: config.dealsLabel,
            dealsHref: config.dealsHref,
            showPrime: config.showPrime,
            primeLabel: config.primeLabel,
            primeHref: config.primeHref,
            showCompare: config.showCompare,
            compareLabel: config.compareLabel,
            compareHref: config.compareHref,
            customLinks: Array.isArray(config.customLinks)
              ? (config.customLinks as never)
              : []
          }}
        />
      )}
    </AdminShell>
  );
}